
import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { formatDateForDisplay } from '@/utils/dateFormatters';

const PrivacyPolicy = () => {
  return (
    <div className="container mx-auto p-4 max-w-4xl">
      <h1 className="text-3xl font-bold mb-6">Privacy Policy</h1>
      
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Information We Collect</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <h3 className="font-semibold mb-2">Personal Information</h3>
              <p className="text-gray-600">
                When you subscribe to our bin washing service, we collect your name, email address, 
                phone number, property address, and postcode to provide our services effectively.
              </p>
            </div>
            <div>
              <h3 className="font-semibold mb-2">Payment Information</h3>
              <p className="text-gray-600">
                Payment processing is handled securely by Stripe. We do not store your credit card 
                details on our servers. Stripe may collect payment information according to their 
                own privacy policy.
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>How We Use Your Information</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="list-disc list-inside text-gray-600 space-y-2">
              <li>To provide and maintain our bin washing service</li>
              <li>To process payments and manage subscriptions</li>
              <li>To communicate with you about your service</li>
              <li>To send service updates and important notifications</li>
              <li>To respond to customer inquiries and support requests</li>
              <li>To improve our services and customer experience</li>
            </ul>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Information Sharing</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-600">
              We do not sell, trade, or otherwise transfer your personal information to third parties 
              except as described in this policy. We may share information with:
            </p>
            <ul className="list-disc list-inside text-gray-600 space-y-2 mt-4">
              <li>Service providers who assist in our operations (e.g., Stripe for payments)</li>
              <li>Legal authorities when required by law</li>
              <li>Professional advisors under confidentiality agreements</li>
            </ul>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Data Security</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-600">
              We implement appropriate security measures to protect your personal information against 
              unauthorized access, alteration, disclosure, or destruction. Our website uses SSL 
              encryption, and we follow industry best practices for data protection.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Your Rights</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-600 mb-4">You have the right to:</p>
            <ul className="list-disc list-inside text-gray-600 space-y-2">
              <li>Access your personal data we hold</li>
              <li>Request correction of inaccurate information</li>
              <li>Request deletion of your personal data</li>
              <li>Object to processing of your personal data</li>
              <li>Request data portability</li>
              <li>Withdraw consent at any time</li>
            </ul>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Cookies</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-600">
              Our website may use cookies to enhance user experience. These are small files stored 
              on your device that help us understand how you use our website and improve our services.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Contact Us</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-600">
              If you have any questions about this Privacy Policy or wish to exercise your rights, 
              please contact us at:
            </p>
            <div className="mt-4 p-4 bg-gray-50 rounded-md">
              <p className="font-medium">Email: contact@binsandthings.com</p>
              <p className="text-sm text-gray-600 mt-2">
                We will respond to your inquiry within 30 days.
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Terms of Service</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <h3 className="font-semibold mb-2">Service Description</h3>
              <p className="text-gray-600">
                Bins and Things provides professional bin washing services to residential and commercial customers. 
                Our service includes regular cleaning and sanitization of waste bins according to your subscription plan.
              </p>
            </div>

            <div>
              <h3 className="font-semibold mb-2">Subscription Terms</h3>
              <ul className="list-disc list-inside text-gray-600 space-y-1">
                <li>Subscriptions are billed in advance on a recurring basis</li>
                <li>Service frequency depends on your chosen plan (weekly, fortnightly, or monthly)</li>
                <li>Cancellation requires 24 hours notice before your next scheduled service</li>
                <li>We reserve the right to refuse service for safety or health reasons</li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold mb-2">Customer Responsibilities</h3>
              <ul className="list-disc list-inside text-gray-600 space-y-1">
                <li>Ensure bins are accessible on scheduled service days</li>
                <li>Provide accurate contact and address information</li>
                <li>Notify us of any changes to service requirements</li>
                <li>Ensure bins are not overloaded or contain hazardous materials</li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold mb-2">Payment Terms</h3>
              <p className="text-gray-600">
                Payment is processed securely through Stripe. Subscriptions are charged automatically according to your billing cycle. 
                Failed payments may result in service suspension until payment is resolved.
              </p>
            </div>

            <div>
              <h3 className="font-semibold mb-2">Limitation of Liability</h3>
              <p className="text-gray-600">
                Our liability is limited to the cost of the service provided. We are not responsible for damage to bins 
                due to normal wear, pre-existing damage, or customer misuse. We maintain appropriate insurance coverage for our services.
              </p>
            </div>

            <div>
              <h3 className="font-semibold mb-2">Service Modifications</h3>
              <p className="text-gray-600">
                We reserve the right to modify our services, pricing, or terms with reasonable notice. 
                Existing customers will be notified of any changes that affect their current subscription.
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Updates to This Policy</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-600">
              We may update this Privacy Policy and Terms of Service from time to time. Any changes will be posted on 
              this page with an updated effective date. We encourage you to review this policy 
              periodically.
            </p>
            <p className="text-sm text-gray-500 mt-4">
              Last updated: {formatDateForDisplay(new Date())}
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
